import { localDateString } from '../../../../../utils/helpers/localDate';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import FormInput from '../../../../../components/child/FormInput';
import FormSelect from '../../../../../components/child/FormSelect';
import FormButton from '../../../../../components/child/FormButton';
import {
    getTariffRatesByHead,
    createTariffRate,
    updateTariffRate,
    updateTariffRateStatus
} from '../../../../../redux/actions/SocietyPortal/TariffRateAction/TariffRateAction';
import { formatIstDate } from '../../../../../utils/helpers/istDate';

const TariffRateModal = ({ show, handleClose, chargeHead }) => {
    const dispatch = useDispatch();
    const {
        tariffRatesByHeadData: rates,
        loadingByHead: loading,
        createLoading,
        updateLoading,
        deleteLoading,
        statusLoading
    } = useSelector((state) => state.TariffRateReducer);
    const { tariffHeadsData } = useSelector((state) => state.TariffHeadReducer);

    const [showAddForm, setShowAddForm] = useState(false);
    const [editingRate, setEditingRate] = useState(null);
    const [formData, setFormData] = useState({
        rate_value: '',
        formula_expression: '',
        effective_from: '',
        effective_to: '',
        unit_type_filter: 'ALL',
        status: 1
    });

    // Determine if we should show formula input based on calculation_type
    const isFormulaType = chargeHead?.calculation_type === 'FORMULA';

    // A head restricted to one property type only ever bills that type, so seed the rate's
    // filter to match. This is the rate to use, not whether the head applies - they are separate.
    const defaultUnitTypeFilter = chargeHead?.unit_type && chargeHead.unit_type !== 'ALL'
        ? chargeHead.unit_type
        : 'ALL';

    const unitTypeOptions = [
        { value: 'ALL', label: 'All Units' },
        { value: 'COMMERCIAL', label: 'Commercial' },
        { value: 'RESIDENTIAL', label: 'Residential' }
    ];

    // Fetch rates when modal opens
    useEffect(() => {
        if (show && chargeHead?.head_id) {
            dispatch(getTariffRatesByHead({ head_id: chargeHead.head_id }));
        }
    }, [show, chargeHead, dispatch]);

    const resetForm = useCallback(() => {
        setShowAddForm(false);
        setEditingRate(null);
        setFormData({
            rate_value: '',
            formula_expression: '',
            effective_from: localDateString(),
            effective_to: '',
            unit_type_filter: defaultUnitTypeFilter,
            status: 1
        });
    }, [defaultUnitTypeFilter]);

    // Reset form when closing
    useEffect(() => {
        if (!show) {
            resetForm();
        }
    }, [show, resetForm]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEdit = (rate) => {
        setEditingRate(rate);
        setFormData({
            rate_value: rate.rate_value || '',
            formula_expression: rate.formula_expression || '',
            effective_from: rate.effective_from ? rate.effective_from.split('T')[0] : '',
            effective_to: rate.effective_to ? rate.effective_to.split('T')[0] : '',
            unit_type_filter: rate.unit_type_filter,
            status: rate.status
        });
        setShowAddForm(true);
    };

    const handleDelete = (rate) => {
        if (window.confirm('Are you sure you want to delete this rate?')) {
            dispatch(updateTariffRateStatus({
                id: rate.rate_id,
                status: 0,
                onSuccess: () => {
                    dispatch(getTariffRatesByHead({ head_id: chargeHead.head_id }));
                }
            }));
        }
    };

    const handleStatusToggle = (rate) => {
        dispatch(updateTariffRateStatus({
            id: rate.rate_id,
            status: rate.status === 1 ? 2 : 1,
            onSuccess: () => {
                dispatch(getTariffRatesByHead({ head_id: chargeHead.head_id }));
            }
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Effective To is optional, but when set it must be after Effective From
        if (formData.effective_to && new Date(formData.effective_to) <= new Date(formData.effective_from)) {
            alert('Effective To date must be greater than the Effective From date');
            return;
        }

        // Build payload based on calculation type
        const payload = {
            head_id: chargeHead.head_id,
            effective_from: formData.effective_from,
            effective_to: formData.effective_to || null,
            unit_type_filter: formData.unit_type_filter,
            status: formData.status
        };

        // Set rate_value or formula_expression based on calculation type
        if (isFormulaType) {
            payload.formula_expression = formData.formula_expression || null;
            payload.rate_value = null;
        } else {
            payload.rate_value = formData.rate_value ? parseFloat(formData.rate_value) : null;
            payload.formula_expression = null;
        }

        if (editingRate) {
            dispatch(updateTariffRate({
                ...payload,
                id: editingRate.rate_id,
                onSuccess: () => {
                    dispatch(getTariffRatesByHead({ head_id: chargeHead.head_id }));
                    resetForm();
                }
            }));
        } else {
            dispatch(createTariffRate({
                ...payload,
                onSuccess: () => {
                    dispatch(getTariffRatesByHead({ head_id: chargeHead.head_id }));
                    resetForm();
                }
            }));
        }
    };

    const insertVariable = (varKey) => {
        const input = document.getElementById('formula_expression_input');
        const start = input?.selectionStart || formData.formula_expression.length;
        const end = input?.selectionEnd || formData.formula_expression.length;
        const current = formData.formula_expression;
        const newVal = current.substring(0, start) + varKey + current.substring(end);
        setFormData({ ...formData, formula_expression: newVal });
        setTimeout(() => { input?.focus(); input?.setSelectionRange(start + varKey.length, start + varKey.length); }, 0);
    };

    const getUnitTypeColor = (type) => {
        switch (type) {
            case 'ALL': return { bg: '#dbeafe', color: '#2563eb' };
            case 'COMMERCIAL': return { bg: '#fef3c7', color: '#d97706' };
            case 'RESIDENTIAL': return { bg: '#dcfce7', color: '#16a34a' };
            default: return { bg: '#f3f4f6', color: '#6b7280' };
        }
    };

    const filteredRates = rates.filter(rate => rate.status !== 0);

    if (!show) return null;

    return (
        <div className="modal-backdrop-custom" onClick={handleClose}>
            <div className="modal-content-custom card radius-16 border-0 shadow-lg" style={{ maxWidth: '900px' }} onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="card-header py-10 px-16 border-bottom d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                        <div className="w-44-px h-44-px rounded-circle bg-primary-100 d-flex align-items-center justify-content-center">
                            <Icon icon="mdi:currency-inr" className="text-primary-600 text-2xl" />
                        </div>
                        <div>
                            <h5 className="fw-semibold mb-1">Tariff Rates</h5>
                            <p className="text-secondary-light text-sm mb-0">{chargeHead?.head_name} ({chargeHead?.head_code})</p>
                        </div>
                    </div>
                    <button type="button" className="btn-close" onClick={handleClose} aria-label="Close"></button>
                </div>

                {/* Modal Body */}
                <div className="card-body p-24">
                    {/* Info Banner */}
                    {isFormulaType && (
                        <div className="alert alert-info d-flex align-items-center gap-2 mb-20">
                            <Icon icon="mdi:function-variant" className="text-xl" />
                            <span className="text-sm">This charge head uses formula-based calculation.</span>
                        </div>
                    )}

                    {/* Rates List */}
                    {loading ? (
                        <div className="d-flex justify-content-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : filteredRates.length > 0 ? (
                        <div className="table-responsive mb-20">
                            <table className="table table-hover mb-0">
                                <thead className="bg-neutral-50">
                                    <tr>
                                        <th className="py-12 px-16 text-secondary-light text-sm fw-semibold">#</th>
                                        <th className="py-12 px-16 text-secondary-light text-sm fw-semibold">Rate / Formula</th>
                                        <th className="py-12 px-16 text-secondary-light text-sm fw-semibold">From</th>
                                        <th className="py-12 px-16 text-secondary-light text-sm fw-semibold">To</th>
                                        <th className="py-12 px-16 text-secondary-light text-sm fw-semibold">Unit Type</th>
                                        <th className="py-12 px-16 text-secondary-light text-sm fw-semibold text-center">Status</th>
                                        <th className="py-12 px-16 text-secondary-light text-sm fw-semibold text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRates.map((rate, index) => {
                                        const typeColor = getUnitTypeColor(rate.unit_type_filter);
                                        return (
                                            <tr key={rate.rate_id} className={rate.status === 2 ? 'opacity-60' : ''}>
                                                <td className="py-12 px-16 align-middle">
                                                    <span className="text-secondary-light">{index + 1}</span>
                                                </td>
                                                <td className="py-12 px-16 align-middle">
                                                    {rate.formula_expression ? (
                                                        <span className="badge bg-warning-50 text-warning-600 fw-medium px-10 py-6 radius-4">
                                                            <Icon icon="mdi:function-variant" className="me-1" style={{ fontSize: '14px' }} />
                                                            {rate.formula_expression}
                                                        </span>
                                                    ) : (
                                                        <span className="fw-semibold text-success-600">
                                                            ₹{parseFloat(rate.rate_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-12 px-16 align-middle text-sm">
                                                    {rate.effective_from ? formatIstDate(rate.effective_from) : '-'}
                                                </td>
                                                <td className="py-12 px-16 align-middle text-sm text-secondary-light">
                                                    {rate.effective_to ? formatIstDate(rate.effective_to) : 'No end'}
                                                </td>
                                                <td className="py-12 px-16 align-middle">
                                                    <span
                                                        className="badge px-10 py-6 radius-4 fw-medium"
                                                        style={{ backgroundColor: typeColor.bg, color: typeColor.color }}
                                                    >
                                                        {rate.unit_type_filter}
                                                    </span>
                                                </td>
                                                <td className="py-12 px-16 align-middle text-center">
                                                    <div className="form-check form-switch d-inline-flex justify-content-center mb-0">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={rate.status === 1}
                                                            onChange={() => handleStatusToggle(rate)}
                                                            disabled={statusLoading}
                                                            style={{ width: '36px', height: '18px' }}
                                                        />
                                                    </div>
                                                </td>
                                                <td className="py-12 px-16 align-middle text-center">
                                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-primary-50 text-primary-600 p-1"
                                                            onClick={() => handleEdit(rate)}
                                                            title="Edit"
                                                        >
                                                            <Icon icon="mdi:pencil" className="text-lg" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger-50 text-danger-600 p-1"
                                                            onClick={() => handleDelete(rate)}
                                                            disabled={deleteLoading}
                                                            title="Delete"
                                                        >
                                                            <Icon icon="mdi:delete" className="text-lg" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-4 mb-20">
                            <Icon icon="mdi:currency-inr" className="text-secondary-light text-4xl mb-2" style={{ opacity: 0.3 }} />
                            <p className="text-secondary-light text-sm mb-0">No rates configured yet</p>
                        </div>
                    )}

                    {/* Add/Edit Form */}
                    {showAddForm ? (
                        <div className="border radius-12 p-20 bg-neutral-50">
                            <div className="d-flex align-items-center justify-content-between mb-16">
                                <h6 className="fw-semibold mb-0">{editingRate ? 'Edit Rate' : 'Add New Rate'}</h6>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={resetForm}
                                ></button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="row g-3">
                                    {isFormulaType ? (
                                        <div className="col-12">
                                            <label className="form-label text-sm fw-medium mb-1">
                                                Formula Expression
                                                <span className="text-danger ms-1">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-white border-end-0">
                                                    <Icon icon="mdi:function-variant" className="text-secondary-light" style={{ fontSize: '16px' }} />
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control border-start-0"
                                                    id="formula_expression_input"
                                                    name="formula_expression"
                                                    value={formData.formula_expression}
                                                    onChange={handleInputChange}
                                                    placeholder="e.g., area * 2.5 + construction_cost / no_of_flats"
                                                    required
                                                />
                                            </div>
                                            <div className="mt-2 p-12 bg-white radius-8 border">
                                                <small className="text-secondary-light fw-semibold d-block mb-2">
                                                    Available Variables <span className="text-xs fw-normal">(click to insert)</span>
                                                </small>
                                                {/* Grouped so "built-up area" is never ambiguous: the flat's own
                                                    area (per flat, what most charges use) vs. the whole wing's total.
                                                    Older formulas using area / total_builtup_area keep working. */}
                                                {[
                                                    {
                                                        group: 'This flat',
                                                        vars: [
                                                            { key: 'flat_builtup_area', label: "Flat's built-up area (sq ft)" },
                                                            { key: 'carpet_area', label: "Flat's carpet area (sq ft)" },
                                                            { key: 'super_built_up_area', label: "Flat's super built-up area (sq ft)" },
                                                            { key: 'parking_slot', label: 'Parking slots allotted to the flat' },
                                                            { key: 'two_wheeler_count', label: "Flat's 2-wheelers" },
                                                            { key: 'four_wheeler_count', label: "Flat's 4-wheelers" },
                                                        ],
                                                    },
                                                    {
                                                        group: 'Whole wing',
                                                        vars: [
                                                            { key: 'wing_builtup_area', label: "Wing's total built-up area (sq ft)" },
                                                            { key: 'no_of_flats', label: 'Number of flats in the wing' },
                                                            { key: 'construction_cost', label: "Wing's construction cost" },
                                                            { key: 'no_of_water_inlets', label: "Wing's water inlets" },
                                                        ],
                                                    },
                                                    {
                                                        group: 'Billing',
                                                        vars: [
                                                            { key: 'duration', label: 'Months in the billing period' },
                                                        ],
                                                    },
                                                ].map((g) => (
                                                    <div key={g.group} className="mb-2">
                                                        <div className="text-xs text-secondary-light mb-1">{g.group}</div>
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {g.vars.map((v) => (
                                                                <span
                                                                    key={v.key}
                                                                    className="badge bg-neutral-200 text-secondary-light text-xs fw-medium px-8 py-4"
                                                                    style={{ cursor: 'pointer' }}
                                                                    title={`${v.label} - click to insert`}
                                                                    onClick={() => insertVariable(v.key)}
                                                                >
                                                                    <code>{v.key}</code> <span className="opacity-75">{v.label}</span>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                                {/(^|[^a-z_])(area|total_builtup_area)([^a-z_]|$)/.test(formData.formula_expression || '') && (
                                                    <div className="text-xs text-warning-main mb-2">
                                                        Note: <code>area</code> is the flat's built-up area and <code>total_builtup_area</code> is the whole wing's.
                                                        Use <code>flat_builtup_area</code> / <code>wing_builtup_area</code> to make it explicit.
                                                    </div>
                                                )}
                                                <div className="d-flex flex-wrap gap-1">
                                                    {/* Head codes from other charge heads of same type */}
                                                    {tariffHeadsData?.filter(h => h.head_id !== chargeHead?.head_id && h.head_code && h.head_type_id === chargeHead?.head_type_id).map((h) => (
                                                        <span
                                                            key={h.head_code}
                                                            className="badge bg-primary-50 text-primary-600 text-xs fw-medium px-8 py-4"
                                                            style={{ cursor: 'pointer' }}
                                                            title={`Head: ${h.head_name}`}
                                                            onClick={() => insertVariable(h.head_code)}
                                                        >
                                                            <code>{h.head_code}</code> <span className="opacity-75">({h.head_name})</span>
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="col-md-6">
                                            <FormInput
                                                label="Rate Value (₹)"
                                                type="number"
                                                name="rate_value"
                                                value={formData.rate_value}
                                                onChange={handleInputChange}
                                                placeholder="0.00"
                                                required={true}
                                                step="0.01"
                                                min="0"
                                            />
                                        </div>
                                    )}

                                    {/* Effective From */}
                                    <div className="col-md-6">
                                        <FormInput
                                            label="Effective From"
                                            type="date"
                                            name="effective_from"
                                            value={formData.effective_from}
                                            onChange={handleInputChange}
                                            required={true}
                                        />
                                    </div>

                                    {/* Effective To */}
                                    <div className="col-md-6">
                                        <FormInput
                                            label="Effective To (Optional)"
                                            type="date"
                                            name="effective_to"
                                            value={formData.effective_to}
                                            onChange={handleInputChange}
                                            min={formData.effective_from || undefined}
                                        />
                                    </div>

                                    {/* Unit Type */}
                                    <div className="col-md-6">
                                        <FormSelect
                                            label="Unit Type"
                                            name="unit_type_filter"
                                            value={formData.unit_type_filter}
                                            onChange={handleInputChange}
                                            options={unitTypeOptions}
                                            required={true}
                                        />
                                    </div>
                                </div>

                                {/* Form Actions */}
                                <div className="d-flex justify-content-end gap-2 mt-20">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary px-20"
                                        onClick={resetForm}
                                    >
                                        Cancel
                                    </button>
                                    <FormButton
                                        type="submit"
                                        variant="primary"
                                        isLoading={createLoading || updateLoading}
                                    >
                                        {editingRate ? 'Update Rate' : 'Add Rate'}
                                    </FormButton>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-primary d-flex align-items-center gap-2 w-100 justify-content-center py-12"
                            onClick={() => {
                                setFormData(prev => ({ ...prev, unit_type_filter: defaultUnitTypeFilter }));
                                setShowAddForm(true);
                            }}
                        >
                            <Icon icon="mdi:plus-circle" className="text-xl" />
                            Add New Rate
                        </button>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="card-footer py-10 px-16 border-top d-flex justify-content-end">
                    <button
                        type="button"
                        className="btn btn-outline-secondary px-24"
                        onClick={handleClose}
                    >
                        Close
                    </button>
                </div>
            </div>

            {/* Custom Styles */}
            <style>{`
                .modal-backdrop-custom {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1050;
                    animation: fadeIn 0.2s ease;
                }

                .modal-content-custom {
                    width: 100%;
                    margin: 16px;
                    animation: slideUp 0.3s ease;
                    max-height: 90vh;
                    overflow-y: auto;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .w-44-px { width: 44px; }
                .h-44-px { height: 44px; }
            `}</style>
        </div>
    );
};

export default TariffRateModal;
